import { useEffect, useRef, useState } from "react";
import { Modal } from "bootstrap";

export default function MaxDetailModal({
  show,
  onClose,
  gameList,
  bet,
  onBatchUpdate,
}) {
  const modalRef = useRef(null);
  const modalInstance = useRef(null);

  const [localList, setLocalList] = useState([]);
  const [search, setSearch] = useState("");
  const [batchAmount, setBatchAmount] = useState("");
  const [batchLoading, setBatchLoading] = useState(false);

  const amountLabel = bet == 0 ? "당첨제한 금액" : "베팅제한 금액";

  useEffect(() => {
    if (show) {
      setLocalList(gameList.map((g) => ({ ...g })));
      setSearch("");
      setBatchAmount("");
    }
  }, [show, gameList]);

  const handleClose = () => modalInstance.current?.hide();

  useEffect(() => {
    if (!modalRef.current) return;

    if (show) {
      modalInstance.current = new Modal(modalRef.current, {
        backdrop: "static",
        keyboard: true,
      });

      modalInstance.current.show();
      modalRef.current.addEventListener("hidden.bs.modal", onClose);

      return () => {
        modalRef.current?.removeEventListener("hidden.bs.modal", onClose);
      };
    }
  }, [show]);

  const handleSearch = (e) => {
    const keyword = e.target.value.toLowerCase();
    setSearch(keyword);

    if (!keyword) {
      setLocalList(gameList.map((g) => ({ ...g })));
      return;
    }

    const filtered = gameList.filter(
      (g) =>
        (g.nameKR || "").toLowerCase().includes(keyword) ||
        (g.nameEN || "").toLowerCase().includes(keyword),
    );

    setLocalList(filtered.map((g) => ({ ...g })));
  };

  const handleBatchApply = async () => {
    const num = String(batchAmount).replace(/[^0-9]/g, "");
    if (!num) {
      alert("일괄 적용할 금액을 입력해주세요");
      return;
    }
    if (localList.length === 0) {
      alert("적용할 게임이 없습니다");
      return;
    }
    if (
      !window.confirm(
        `현재 목록 ${localList.length}개 게임에 ${amountLabel} ${Number(
          num,
        ).toLocaleString()} 원을 일괄 적용할까요?`,
      )
    ) {
      return;
    }

    setBatchLoading(true);
    try {
      if (onBatchUpdate) {
        await onBatchUpdate(
          Number(num),
          localList.map((g) => g.gameId),
        );
      } else {
        for (const game of localList) {
          await game.onUpdate?.(Number(num));
        }
      }

      setLocalList((prev) =>
        prev.map((g) =>
          bet == 0 ? { ...g, maxi: num } : { ...g, bet_maxi: num },
        ),
      );
      alert("일괄 적용 완료");
    } catch (err) {
      console.error(err);
      alert("일괄 적용 중 오류가 발생했습니다");
    } finally {
      setBatchLoading(false);
    }
  };

  return (
    <div
      className="modal fade"
      id="maxDetailModal"
      tabIndex="-1"
      ref={modalRef}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
        style={{ maxWidth: "95vw", width: "1400px", height: "90vh" }}
      >
        <div className="modal-content" style={{ height: "90vh" }}>
          <div className="modal-header">
            <h5 className="modal-title">
              {bet == 0
                ? "최대 당첨금액 게임별 설정"
                : "최대 베팅금액 게임별 설정"}
            </h5>
            <button className="btn-close" onClick={handleClose}></button>
          </div>

          <div className="modal-body d-flex flex-column" style={{ minHeight: 0 }}>
            <div className="mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="게임명 검색 (KR / EN)"
                value={search}
                onChange={handleSearch}
              />
            </div>

            <div className="mb-3">
              <div className="input-group" style={{ maxWidth: 520 }}>
                <span className="input-group-text">{amountLabel} 일괄변경</span>
                <input
                  type="text"
                  className="form-control"
                  inputMode="numeric"
                  style={{ textAlign: "right" }}
                  placeholder="금액 입력"
                  value={batchAmount}
                  onChange={(e) =>
                    setBatchAmount(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  disabled={batchLoading}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleBatchApply}
                  disabled={batchLoading}
                >
                  <i className="fas fa-bolt" />{" "}
                  {batchLoading ? "적용 중..." : "일괄적용"}
                </button>
              </div>
            </div>

            <div className="table-responsive flex-grow-1" style={{ minHeight: 0 }}>
              <table className="table table-small-font table-bordered table-striped align-middle mb-0">
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: "28%" }}>
                      게임명(KR)
                    </th>
                    <th className="text-center" style={{ width: "28%" }}>
                      게임명(EN)
                    </th>
                    <th className="text-center" style={{ width: "44%" }}>
                      {amountLabel}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {localList.map((game, idx) => {
                    const currentValue =
                      bet == 0 ? game.maxi ?? "" : game.bet_maxi ?? "";
                    return (
                      <tr key={game.gameId || idx}>
                        <td className="text-center">{game.nameKR}</td>
                        <td className="text-center">{game.nameEN}</td>
                        <td>
                          <div className="input-group input-group-sm flex-nowrap">
                            <input
                              className="form-control"
                              type="text"
                              inputMode="numeric"
                              style={{ textAlign: "right", minWidth: 140 }}
                              value={currentValue}
                              onChange={(e) => {
                                const raw = e.target.value.replace(
                                  /[^0-9]/g,
                                  "",
                                );
                                setLocalList((prev) =>
                                  prev.map((item, i) => {
                                    if (i !== idx) return item;
                                    return bet == 0
                                      ? { ...item, maxi: raw }
                                      : { ...item, bet_maxi: raw };
                                  }),
                                );
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-primary text-nowrap"
                              onClick={() => game.onUpdate?.(currentValue)}
                            >
                              <i className="fas fa-edit" /> 수정
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-danger text-nowrap"
                              onClick={() => game.onReset?.()}
                            >
                              <i className="fas fa-redo-alt" /> 초기화
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
